/**
 * RealtimeVoiceTutorPanel — student-only Realtime Voice Tutor UI (P0-4 Phase 2)
 *
 * Mount guards (defense-in-depth — also enforced at Dashboard.js render site):
 *   - CRITICAL-2: returns null if !sessionId || !studentProfile.
 *   - #11 student-only: returns null when `isTeacherMode === true`.
 *
 * M3 CUD 4-state token mapping (NO new color tokens added):
 *   idle       → --text-muted   (neutral / not started)
 *   connecting → --warning      (in-progress)
 *   connected  → --success      (active OK)
 *   error      → --danger       (failure)
 *
 * Selector contract — 13 data-testid (kept stable for 工部 spec):
 *   realtime-voice-panel, connection-state, voice-select,
 *   connect-button, disconnect-button, error-banner,
 *   ai-caption, renew-prompt, renew-confirm, renew-decline,
 *   remote-audio, student-captions, student-caption-{itemId}
 *
 * UI text — modelname-free contract: NO `OpenAI` / `gpt-` / `Claude` / `claude-`
 * / `Sonnet` / `Opus` / `Realtime` literals. Generic JP phrasing only.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import useRealtimeVoice from '../hooks/useRealtimeVoice';
import { REALTIME_VOICE_OPTIONS } from '../utils/realtimeVoiceConstants';
import FuriganaText from './FuriganaText';

// 4-state → CSS variable token map (intentionally uses ONLY existing tokens).
const STATE_COLOR_TOKEN = {
  idle: 'var(--text-muted)',
  connecting: 'var(--warning)',
  connected: 'var(--success)',
  error: 'var(--danger)',
};

function RealtimeVoiceTutorPanel({ sessionId, studentProfile, onApplyOverlay, isTeacherMode }) {
  // Hook called UNCONDITIONALLY (rules-of-hooks). The hook itself does not
  // open any media / network resources until `connect()` is explicitly invoked
  // by the user, so calling it for an unmounted-panel case is harmless.
  //
  // e2e-completion post-merge hotfix P1-1: prop renamed `graphId` → `sessionId`
  // to align with backend ownership-pinning anchor. Dashboard.js now passes
  // `currentSession.id` (integer PK) instead of `currentSession.graph_id`
  // (string). See CHANGELOG `[v2.14.4-p0-4-phase2-e2e-completion-post-merge-hotfix]`.
  const { t } = useTranslation();
  const {
    connectionState,
    errorCode,
    selectedVoice,
    setSelectedVoice,
    aiCaptionText,
    studentTranscripts,
    showRenewPrompt,
    sessionEndingSoon,
    sessionLimitNotice,
    remoteAudioRef,
    connect,
    disconnect,
    renewSession,
  } = useRealtimeVoice({ sessionId, studentProfile, onApplyOverlay });

  // CRITICAL-2 + #11 defense-in-depth (mount guard) — placed AFTER all hook
  // calls per React rules-of-hooks. Dashboard.js render site also gates this,
  // so under normal usage this branch never fires.
  if (isTeacherMode) return null;
  if (!sessionId || !studentProfile) return null;

  const stateColor = STATE_COLOR_TOKEN[connectionState] || STATE_COLOR_TOKEN.idle;
  const stateLabel = t(`voice.state.${connectionState}`, { defaultValue: t('voice.state.idle') });
  const isBusy = connectionState === 'connecting' || connectionState === 'connected';
  const isDisabled = !sessionId || !studentProfile;

  // Phase 3 Option A — student-utterance caption entries. Hook exposes a
  // per-`item_id` map (P2 turn-safety); `Object.entries` preserves insertion
  // order for string keys per ES2015+, so utterances render in arrival order.
  // Test coverage: TC-P3-1..4 in `e2e/p0-4-phase2-realtime-voice/edge-cases.spec.js`.
  const studentEntries = Object.entries(studentTranscripts);

  return (
    <div
      data-testid="realtime-voice-panel"
      className="rounded-xl p-3 mt-2"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>
          <FuriganaText text={t('voice.title')} />
        </h3>
        <span
          data-testid="connection-state"
          className="text-xs"
          style={{ color: stateColor, fontWeight: 600 }}
        >
          <FuriganaText text={stateLabel} />
        </span>
      </div>

      <div className="mb-2">
        <label
          className="text-xs block mb-1"
          htmlFor="realtime-voice-select"
          style={{ color: 'var(--text-muted)' }}
        >
          <FuriganaText text={t('voice.selectVoice')} />
        </label>
        <select
          id="realtime-voice-select"
          data-testid="voice-select"
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          disabled={isBusy}
          className="w-full p-1.5 rounded"
          style={{
            background: 'var(--bg-base)',
            border: '1px solid var(--border)',
            color: 'var(--text)',
          }}
        >
          {REALTIME_VOICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{t(`voice.options.${o.value}`)}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2 mb-2">
        <button
          type="button"
          data-testid="connect-button"
          onClick={connect}
          disabled={isDisabled || isBusy}
          className="flex-1 px-3 py-1.5 rounded font-semibold"
          style={{
            background: isBusy ? 'var(--text-muted)' : 'var(--primary)',
            color: 'var(--bg-surface)',
            opacity: isDisabled ? 0.5 : 1,
            cursor: isDisabled || isBusy ? 'not-allowed' : 'pointer',
          }}
        >
          <FuriganaText text={connectionState === 'connecting' ? t('voice.state.connecting') : t('voice.startButton')} />
        </button>
        {isBusy && (
          <button
            type="button"
            data-testid="disconnect-button"
            onClick={disconnect}
            className="px-3 py-1.5 rounded"
            style={{ background: 'var(--bg-muted)', color: 'var(--text)' }}
          >
            <FuriganaText text={t('voice.end')} />
          </button>
        )}
      </div>

      {errorCode && (
        <div
          data-testid="error-banner"
          role="alert"
          className="p-2 rounded mb-2 text-xs"
          style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
        >
          <FuriganaText text={t(`voice.error.${errorCode}`, { defaultValue: t('voice.error.realtime_session_unavailable') })} />
        </div>
      )}

      {/* D4: heads-up ~60s before the session-duration or idle limit ends the call.
          role="alert" (time-sensitive, not the caption double-speak case). */}
      {sessionEndingSoon && (
        <div
          data-testid="session-ending-warning"
          role="alert"
          className="p-2 rounded mb-2 text-xs"
          style={{ background: 'var(--bg-muted)', border: '1px solid var(--warning)', color: 'var(--text)' }}
        >
          <FuriganaText text={t(`voice.sessionEndingSoon.${sessionEndingSoon}`)} />
        </div>
      )}

      {/* D4: post-auto-disconnect notice explaining why the session ended. */}
      {sessionLimitNotice && (
        <div
          data-testid="session-limit-notice"
          role="status"
          className="p-2 rounded mb-2 text-xs"
          style={{ background: 'var(--bg-muted)', color: 'var(--text-muted)' }}
        >
          <FuriganaText text={t(`voice.sessionEnded.${sessionLimitNotice}`)} />
        </div>
      )}

      {/* CRITICAL-1: AI output transcript only (Phase 2 scope).
          A6 (Tier-1 a11y) live-region decision — this region deliberately
          stays NON-live; do not add aria-live here. `aiCaptionText` is
          populated from `response.output_audio_transcript.delta` /
          `.done` data-channel events (see useRealtimeVoice.js
          buildDataChannelHandler), which fire IN LOCKSTEP with the same
          spoken content being played through the hidden
          `<audio autoPlay ref={remoteAudioRef}>` sink below. If this div
          had `aria-live="polite"`, a screen reader would announce the
          caption text via its own TTS at the same moment the real voice
          audio is playing it aloud — an audible double-speak collision,
          which is worse for a screen-reader student than no announcement.
          Correction of an earlier plan assumption: the semantics are NOT
          inverted between the two caption regions in this file — it is
          specifically THIS region (time-locked to concurrent audio) that
          must stay non-live, while the student-own-caption region below
          (no concurrent audio echo of the student's own voice) is the one
          that safely carried aria-live and has since had it removed for a
          separate, unrelated reason (see comment there). */}
      {aiCaptionText && (
        <div
          data-testid="ai-caption"
          className="mt-2 p-2 rounded text-sm"
          style={{ background: 'var(--bg-muted)', color: 'var(--text)' }}
        >
          <div className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('voice.teacherExplanation')} />
          </div>
          <p><FuriganaText text={aiCaptionText} /></p>
        </div>
      )}

      {/* Phase 3 Option A — student-utterance captions (P1-1 sidecar + P2 turn-safety). */}
      {studentEntries.length > 0 && (
        <section
          data-testid="student-captions"
          // role="region" omitted: <section> has implicit ARIA role of
          // "region" when accompanied by aria-label (per HTML/WAI-ARIA spec
          // + eslint-plugin-jsx-a11y/no-redundant-roles). The aria-label
          // below promotes <section> from "generic" to "region" landmark.
          aria-label={t('voice.captionsAriaLabel')}
          // A6 (Tier-1 a11y) live-region decision — aria-live/aria-atomic
          // intentionally REMOVED (previously aria-live="polite" +
          // aria-atomic="false"). This region echoes the STUDENT's own
          // speech back to them as they speak it; the student already
          // knows what they just said, so a continuous live announcement
          // is redundant and would repeatedly interrupt a screen-reader
          // user's focus mid-utterance. The aria-label above keeps this a
          // "region" landmark, so AT users can still navigate to it on
          // demand to review the transcript. This is unrelated to the
          // AI-caption region below, which stays non-live for a different
          // reason (concurrent audio playback, not self-echo — see its
          // comment).
          className="mt-2 p-2 rounded text-sm"
          style={{
            background: 'rgba(var(--primary-rgb), 0.08)',
            borderLeft: '3px solid var(--primary)',
            color: 'var(--text)',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          <div className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('voice.yourSpeech')} />
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {studentEntries.map(([itemId, entry]) => (
              <li
                key={itemId}
                data-testid={`student-caption-${itemId}`}
                aria-busy={!entry.completed}
                style={{
                  fontStyle: entry.completed ? 'normal' : 'italic',
                  wordBreak: 'break-word',
                  marginBottom: '0.25rem',
                }}
              >
                <FuriganaText text={entry.transcript} />
                {!entry.completed && <FuriganaText text={t('voice.typing')} />}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* C10 renewal prompt — surfaced 30s before expires_at. */}
      {showRenewPrompt && (
        <div
          data-testid="renew-prompt"
          role="dialog"
          aria-label={t('voice.renewDialogAria')}
          className="mt-2 p-2 rounded text-xs"
          style={{
            background: 'var(--bg-muted)',
            border: '1px solid var(--warning)',
            color: 'var(--text)',
          }}
        >
          <p className="mb-2">
            <FuriganaText text={t('voice.renewPrompt')} />
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="renew-confirm"
              onClick={renewSession}
              className="px-2 py-1 rounded text-xs"
              style={{ background: 'var(--primary)', color: 'var(--bg-surface)' }}
            >
              <FuriganaText text={t('voice.renew')} />
            </button>
            <button
              type="button"
              data-testid="renew-decline"
              onClick={disconnect}
              className="px-2 py-1 rounded text-xs"
              style={{ background: 'var(--bg-muted)', color: 'var(--text)' }}
            >
              <FuriganaText text={t('voice.end')} />
            </button>
          </div>
        </div>
      )}

      {/* M2: hidden remote audio sink — autoPlay so AI voice is heard. */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        playsInline
        data-testid="remote-audio"
        style={{ display: 'none' }}
      />
    </div>
  );
}

export default RealtimeVoiceTutorPanel;
